import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import HistoryIcon from "@mui/icons-material/History";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import EditNoteIcon from "@mui/icons-material/EditNote";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import API from "../../api/api";

const pockets = [
  ["all_pockets", "🔥 ALL Pockets & Packages"],
  ["main", "Main Wallet"],
  ["total_earning", "Total Earning (Lifetime)"],
  ["add_money", "Add Money Pocket"],
  ["self_package", "Self Rebirth Pocket"],
  ["withdrawal", "Withdrawal Pocket"],
  ["package_purchase_coupon", "Package Coupon Wallet"],
];

function money(v) {
  const n = Number(v || 0);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
}

function formatDate(isoStr) {
  if (!isoStr) return "-";
  try {
    const d = new Date(isoStr);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return isoStr;
  }
}

export default function AdminWallets() {
  const [activeTab, setActiveTab] = useState(0); // 0: Wallets, 1: History
  const [viewMode, setViewMode] = useState("table"); // "table" or "cards"

  // TAB 0: WALLETS STATE
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [adjust, setAdjust] = useState({ user_id: "", pocket: "main", action: "credit", amount: "", note: "" });

  // Dialog state for quick debit all
  const [debitTarget, setDebitTarget] = useState(null);
  const [debitNote, setDebitNote] = useState("");
  const [debitBusy, setDebitBusy] = useState(false);

  // TAB 1: ADJUSTMENT HISTORY STATE
  const [historyRows, setHistoryRows] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(50);
  const [historyQ, setHistoryQ] = useState("");
  const [historyPocket, setHistoryPocket] = useState("");
  const [historyAction, setHistoryAction] = useState("");
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyErr, setHistoryErr] = useState("");

  // Load Wallets
  async function loadWallets() {
    try {
      setLoading(true);
      setErr("");
      const res = await API.get("/admin/wallets/", {
        params: { q, page: page + 1, page_size: rowsPerPage },
      });
      setRows(res?.data?.results || []);
      setTotalCount(res?.data?.count || (res?.data?.results || []).length);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load wallets");
    } finally {
      setLoading(false);
    }
  }

  // Load History
  async function loadHistory() {
    try {
      setHistoryLoading(true);
      setHistoryErr("");
      const res = await API.get("/admin/wallets/adjustments-history/", {
        params: {
          q: historyQ,
          pocket: historyPocket,
          action: historyAction,
          page: historyPage + 1,
          page_size: historyRowsPerPage,
        },
      });
      setHistoryRows(res?.data?.results || []);
      setHistoryTotal(res?.data?.count || 0);
    } catch (e) {
      setHistoryErr(e?.response?.data?.detail || "Failed to load adjustment history");
    } finally {
      setHistoryLoading(false);
    }
  }

  useEffect(() => {
    if (activeTab === 0) {
      loadWallets();
    } else {
      loadHistory();
    }
  }, [activeTab, page, rowsPerPage, historyPage, historyRowsPerPage]);

  async function submitAdjust() {
    try {
      setErr("");
      const payload = {
        ...adjust,
        amount: adjust.action === "debit_all" ? "0.01" : adjust.amount,
      };
      await API.post(`/admin/wallets/${adjust.user_id}/adjust/`, payload);
      setAdjust((x) => ({ ...x, amount: "", note: "" }));
      await loadWallets();
      if (activeTab === 1) await loadHistory();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Adjustment failed");
    }
  }

  async function submitDebitAll() {
    if (!debitTarget) return;
    try {
      setDebitBusy(true);
      setErr("");
      await API.post(`/admin/wallets/${debitTarget.user_id}/adjust/`, {
        pocket: "all_pockets",
        action: "debit_all",
        amount: "0.01",
        note: debitNote || "Admin Flush All Package Balances",
      });
      setDebitTarget(null);
      setDebitNote("");
      await loadWallets();
      if (activeTab === 1) await loadHistory();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Debit all failed");
    } finally {
      setDebitBusy(false);
    }
  }

  const selected = useMemo(() => rows.find((r) => String(r.user_id) === String(adjust.user_id)), [rows, adjust.user_id]);

  return (
    <Box sx={{ p: { xs: 1, md: 2.5 } }}>
      {/* HEADER */}
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 900, display: "flex", alignItems: "center", gap: 1 }}>
            <AccountBalanceWalletIcon color="primary" /> Wallets & Package Balances
          </Typography>
          <Typography sx={{ color: "#64748b", fontSize: 13 }}>
            Manage active consumer pockets (Main, Self Rebirth, Withdrawal, Pkg Coupon, Add Money) and audit historical admin entries.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button component={Link} to="/admin/wallet-vouchers" variant="outlined" size="small">Vouchers</Button>
          <Button component={Link} to="/admin/wallet-reconcile" variant="outlined" size="small">Reconcile</Button>
          <Tooltip title="Reload">
            <IconButton onClick={activeTab === 0 ? loadWallets : loadHistory} color="primary" size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* TABS NAVIGATION */}
      <Paper variant="outlined" sx={{ borderRadius: 2, mb: 2, bgcolor: "#f8fafc" }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab
            icon={<AccountBalanceWalletIcon fontSize="small" />}
            iconPosition="start"
            label="Consumer Wallets (Active Flow)"
            sx={{ fontWeight: 800, textTransform: "none", fontSize: 14 }}
          />
          <Tab
            icon={<HistoryIcon fontSize="small" />}
            iconPosition="start"
            label="Admin Credit & Debit History (Audit Trail)"
            sx={{ fontWeight: 800, textTransform: "none", fontSize: 14 }}
          />
        </Tabs>
      </Paper>

      {err && <Alert severity="error" sx={{ mb: 1.5 }}>{err}</Alert>}
      {loading && <LinearProgress sx={{ mb: 1.5 }} />}

      {/* ======================= TAB 0: CONSUMER WALLETS ======================= */}
      {activeTab === 0 && (
        <>
          {/* MANUAL ADJUSTMENT ENTRY FORM */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: "#ffffff", borderColor: "#e2e8f0" }}>
            <Typography sx={{ fontWeight: 900, mb: 1.5, color: "#1e293b", display: "flex", alignItems: "center", gap: 0.8 }}>
              <EditNoteIcon color="primary" fontSize="small" /> Manual Wallet & Package Entry
            </Typography>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="center">
              <TextField
                select
                size="small"
                label="Target User"
                value={adjust.user_id}
                onChange={(e) => setAdjust((x) => ({ ...x, user_id: e.target.value }))}
                sx={{ minWidth: 220, flex: 1 }}
              >
                <MenuItem value="">Select consumer</MenuItem>
                {rows.map((r) => (
                  <MenuItem key={r.user_id} value={r.user_id}>
                    {r.username} {r.full_name ? `(${r.full_name})` : ""} {r.prefixed_id ? `- ${r.prefixed_id}` : ""}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Pocket"
                value={adjust.pocket}
                onChange={(e) => setAdjust((x) => ({ ...x, pocket: e.target.value }))}
                sx={{ minWidth: 200 }}
              >
                {pockets.map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Action"
                value={adjust.action}
                onChange={(e) => setAdjust((x) => ({ ...x, action: e.target.value }))}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="credit">Credit</MenuItem>
                <MenuItem value="debit">Debit</MenuItem>
                <MenuItem value="debit_all">Debit / Flush ALL Pockets</MenuItem>
              </TextField>
              {adjust.action !== "debit_all" && (
                <TextField
                  size="small"
                  label="Amount (₹)"
                  type="number"
                  value={adjust.amount}
                  onChange={(e) => setAdjust((x) => ({ ...x, amount: e.target.value }))}
                  sx={{ width: { xs: "100%", md: 130 } }}
                />
              )}
              <TextField
                size="small"
                label="Reason / Note"
                value={adjust.note}
                onChange={(e) => setAdjust((x) => ({ ...x, note: e.target.value }))}
                sx={{ flex: 1.5 }}
                placeholder="e.g. Approved manual bonus or adjustment"
              />
              <Button
                variant="contained"
                color={adjust.action === "debit_all" ? "error" : "primary"}
                disabled={!adjust.user_id || (adjust.action !== "debit_all" && !adjust.amount)}
                onClick={submitAdjust}
                sx={{ fontWeight: 800, minWidth: 110, height: 40 }}
              >
                {adjust.action === "debit_all" ? "Flush All" : "Apply"}
              </Button>
            </Stack>
            {selected && (
              <Typography sx={{ mt: 1, fontSize: 12, color: "#64748b" }}>
                Selected: <strong>{selected.full_name || selected.username}</strong> ({selected.username}) | Main: ₹{money(selected.main_balance)} | Self Rebirth: ₹{money(selected.pockets?.self_package)} | Withdrawable: ₹{money(selected.withdrawable_balance)}
              </Typography>
            )}
          </Paper>

          {/* SEARCH & VIEW TOGGLE */}
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.5} alignItems="center">
              <Stack direction="row" spacing={1} sx={{ flex: 1, width: "100%" }}>
                <TextField
                  size="small"
                  placeholder="Search by consumer name, username, mobile, or TR-ID..."
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && loadWallets()}
                  fullWidth
                />
                <Button variant="contained" onClick={loadWallets} sx={{ minWidth: 100, fontWeight: 700 }}>
                  Search
                </Button>
              </Stack>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Tooltip title="Table View">
                  <IconButton
                    color={viewMode === "table" ? "primary" : "default"}
                    onClick={() => setViewMode("table")}
                    size="small"
                  >
                    <ViewListIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Card View">
                  <IconButton
                    color={viewMode === "cards" ? "primary" : "default"}
                    onClick={() => setViewMode("cards")}
                    size="small"
                  >
                    <ViewModuleIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Paper>

          {/* ============= VIEW 1: TABLE VIEW ============= */}
          {viewMode === "table" ? (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: "#f1f5f9" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800, color: "#334155" }}>Consumer</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: "#1e3a8a" }}>Main Wallet</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: "#0f766e" }}>Total Earning</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: "#7c3aed" }}>Self Rebirth (25%)</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: "#047857" }}>Withdrawal Pocket</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: "#b45309" }}>Pkg Coupon</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: "#0369a1" }}>Add Money</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 800, color: "#475569" }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4, color: "#64748b" }}>
                        No consumer wallet records found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((r) => (
                      <TableRow key={r.user_id} hover>
                        <TableCell>
                          <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#0f172a" }}>
                            {r.full_name || r.username}
                          </Typography>
                          <Typography sx={{ color: "#64748b", fontSize: 11, fontFamily: "monospace" }}>
                            {r.username} {r.prefixed_id ? `• ${r.prefixed_id}` : ""}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            size="small"
                            label={`₹${money(r.main_balance)}`}
                            color={Number(r.main_balance) > 0 ? "primary" : "default"}
                            sx={{ fontWeight: 700, fontFamily: "monospace" }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            size="small"
                            label={`₹${money(r.total_earning ?? r.pockets?.total_earning)}`}
                            sx={{
                              fontWeight: 700,
                              fontFamily: "monospace",
                              bgcolor: Number(r.total_earning ?? r.pockets?.total_earning) > 0 ? "#ccfbf1" : "#f1f5f9",
                              color: Number(r.total_earning ?? r.pockets?.total_earning) > 0 ? "#0f766e" : "#64748b",
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            size="small"
                            label={`₹${money(r.self_account_balance ?? r.pockets?.self_package)}`}
                            color={Number(r.self_account_balance ?? r.pockets?.self_package) > 0 ? "secondary" : "default"}
                            sx={{ fontWeight: 700, fontFamily: "monospace" }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            size="small"
                            label={`₹${money(r.withdrawable_balance)}`}
                            color={Number(r.withdrawable_balance) > 0 ? "success" : "default"}
                            sx={{ fontWeight: 700, fontFamily: "monospace" }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            size="small"
                            label={`₹${money(r.pockets?.package_purchase_coupon)}`}
                            color={Number(r.pockets?.package_purchase_coupon) > 0 ? "warning" : "default"}
                            sx={{ fontWeight: 700, fontFamily: "monospace" }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            size="small"
                            label={`₹${money(r.pockets?.add_money)}`}
                            color={Number(r.pockets?.add_money) > 0 ? "info" : "default"}
                            sx={{ fontWeight: 700, fontFamily: "monospace" }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.8} justifyContent="center">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setAdjust((x) => ({ ...x, user_id: r.user_id }))}
                              sx={{ fontSize: 11, py: 0.2, px: 1, textTransform: "none", fontWeight: 700 }}
                            >
                              Quick Adjust
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() => setDebitTarget(r)}
                              sx={{ fontSize: 11, py: 0.2, px: 1, textTransform: "none", fontWeight: 700 }}
                            >
                              Flush
                            </Button>
                            <Button
                              component={Link}
                              to={`/admin/wallets/${r.user_id}`}
                              size="small"
                              variant="text"
                              sx={{ fontSize: 11, py: 0.2, px: 1, textTransform: "none" }}
                            >
                              Detail
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            /* ============= VIEW 2: CARDS VIEW ============= */
            <Stack spacing={1}>
              {rows.map((r) => (
                <Paper key={r.user_id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.5} alignItems="center">
                    <Box>
                      <Typography sx={{ fontWeight: 900 }}>{r.full_name || r.username}</Typography>
                      <Typography sx={{ color: "#64748b", fontSize: 12 }}>
                        {r.username} {r.prefixed_id ? `- ${r.prefixed_id}` : ""}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                      <Chip label={`Main Rs. ${money(r.main_balance)}`} color={Number(r.main_balance) > 0 ? "primary" : "default"} />
                      <Chip
                        label={`Total Earning Rs. ${money(r.total_earning ?? r.pockets?.total_earning)}`}
                        sx={{
                          fontWeight: 700,
                          bgcolor: Number(r.total_earning ?? r.pockets?.total_earning) > 0 ? "#ccfbf1" : "#f1f5f9",
                          color: Number(r.total_earning ?? r.pockets?.total_earning) > 0 ? "#0f766e" : "#64748b",
                        }}
                      />
                      <Chip label={`Self Rebirth Rs. ${money(r.self_account_balance ?? r.pockets?.self_package)}`} color={Number(r.self_account_balance ?? r.pockets?.self_package) > 0 ? "secondary" : "default"} />
                      <Chip label={`Withdrawal Rs. ${money(r.withdrawable_balance)}`} />
                      <Chip label={`Add Money Rs. ${money(r.pockets?.add_money)}`} color={Number(r.pockets?.add_money) > 0 ? "info" : "default"} />
                      <Chip label={`Pkg Coupon Rs. ${money(r.pockets?.package_purchase_coupon)}`} />
                      <Button size="small" variant="outlined" color="error" onClick={() => setDebitTarget(r)}>
                        Debit All Pockets
                      </Button>
                      <Button component={Link} to={`/admin/wallets/${r.user_id}`} size="small" variant="outlined">
                        Detail
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}

          {/* PAGINATION */}
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </>
      )}

      {/* ======================= TAB 1: ADMIN CREDIT & DEBIT HISTORY ======================= */}
      {activeTab === 1 && (
        <Box>
          {/* HISTORY FILTER BAR */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="center">
              <TextField
                size="small"
                label="Search by user, mobile, admin or reason"
                value={historyQ}
                onChange={(e) => setHistoryQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadHistory()}
                fullWidth
              />
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel>Filter by Pocket</InputLabel>
                <Select
                  value={historyPocket}
                  label="Filter by Pocket"
                  onChange={(e) => setHistoryPocket(e.target.value)}
                >
                  <MenuItem value="">All Pockets</MenuItem>
                  <MenuItem value="main">Main Wallet</MenuItem>
                  <MenuItem value="self_package">Self Rebirth Pocket</MenuItem>
                  <MenuItem value="withdrawal">Withdrawal Pocket</MenuItem>
                  <MenuItem value="package_purchase_coupon">Package Coupon Wallet</MenuItem>
                  <MenuItem value="add_money">Add Money Pocket</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Action</InputLabel>
                <Select
                  value={historyAction}
                  label="Action"
                  onChange={(e) => setHistoryAction(e.target.value)}
                >
                  <MenuItem value="">All Actions</MenuItem>
                  <MenuItem value="credit">Credits (+)</MenuItem>
                  <MenuItem value="debit">Debits (-)</MenuItem>
                </Select>
              </FormControl>
              <Button variant="contained" onClick={loadHistory} sx={{ minWidth: 100, fontWeight: 700, height: 40 }}>
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setHistoryQ("");
                  setHistoryPocket("");
                  setHistoryAction("");
                  setTimeout(loadHistory, 0);
                }}
                sx={{ minWidth: 80, height: 40 }}
              >
                Clear
              </Button>
            </Stack>
          </Paper>

          {historyErr && <Alert severity="error" sx={{ mb: 1.5 }}>{historyErr}</Alert>}
          {historyLoading && <LinearProgress sx={{ mb: 1.5 }} />}

          {/* HISTORY DATA TABLE */}
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: "#f1f5f9" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 800, color: "#334155" }}>Date & Time</TableCell>
                  <TableCell sx={{ fontWeight: 800, color: "#334155" }}>Consumer</TableCell>
                  <TableCell sx={{ fontWeight: 800, color: "#334155" }}>Pocket</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, color: "#334155" }}>Adjustment Amount</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, color: "#334155" }}>Balance After</TableCell>
                  <TableCell sx={{ fontWeight: 800, color: "#334155" }}>Admin Operator</TableCell>
                  <TableCell sx={{ fontWeight: 800, color: "#334155" }}>Reason / Note</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historyRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: "#64748b" }}>
                      No manual admin credit or debit records found.
                    </TableCell>
                  </TableRow>
                ) : (
                  historyRows.map((h) => (
                    <TableRow key={h.id} hover>
                      <TableCell sx={{ fontSize: 12, color: "#475569", whiteSpace: "nowrap" }}>
                        {formatDate(h.created_at)}
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#0f172a" }}>
                          {h.full_name || h.username}
                        </Typography>
                        <Typography sx={{ color: "#64748b", fontSize: 11, fontFamily: "monospace" }}>
                          {h.username} {h.prefixed_id ? `• ${h.prefixed_id}` : ""}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={h.pocket_label}
                          sx={{
                            fontWeight: 700,
                            fontSize: 11,
                            bgcolor:
                              h.pocket === "self_package"
                                ? "#f3e8ff"
                                : h.pocket === "withdrawal"
                                ? "#d1fae5"
                                : h.pocket === "package_purchase_coupon"
                                ? "#fef3c7"
                                : h.pocket === "add_money"
                                ? "#e0f2fe"
                                : "#eff6ff",
                            color:
                              h.pocket === "self_package"
                                ? "#6b21a8"
                                : h.pocket === "withdrawal"
                                ? "#065f46"
                                : h.pocket === "package_purchase_coupon"
                                ? "#92400e"
                                : h.pocket === "add_money"
                                ? "#0369a1"
                                : "#1e40af",
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          size="small"
                          label={`${h.action === "credit" ? "+" : "-"} ₹${money(h.amount)}`}
                          color={h.action === "credit" ? "success" : "error"}
                          sx={{ fontWeight: 800, fontFamily: "monospace" }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontFamily: "monospace", fontSize: 12, color: "#334155" }}>
                        ₹{money(h.balance_after)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={h.by_admin || "admin"}
                          sx={{ fontWeight: 700, fontSize: 11 }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, color: "#334155", maxWidth: 280 }}>
                        {h.note || "-"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* HISTORY PAGINATION */}
          <TablePagination
            component="div"
            count={historyTotal}
            page={historyPage}
            onPageChange={(_, p) => setHistoryPage(p)}
            rowsPerPage={historyRowsPerPage}
            onRowsPerPageChange={(e) => {
              setHistoryRowsPerPage(parseInt(e.target.value, 10));
              setHistoryPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </Box>
      )}

      {/* DIALOG FOR QUICK DEBIT ALL */}
      <Dialog open={Boolean(debitTarget)} onClose={() => setDebitTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 900, color: "#b91c1c", display: "flex", alignItems: "center", gap: 1 }}>
          <DeleteSweepIcon color="error" /> Flush All Package & Wallet Balances
        </DialogTitle>
        <DialogContent dividers>
          {debitTarget && (
            <Box sx={{ mb: 1.5 }}>
              <Typography fontWeight={700}>
                {debitTarget.full_name || debitTarget.username} ({debitTarget.username})
              </Typography>
              <Typography variant="caption" color="text.secondary">
                This will debit all non-zero balances across Main Wallet, Add Money, Self Rebirth, Withdrawal, and Package Coupon pockets for this user.
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            size="small"
            label="Reason for removal"
            value={debitNote}
            onChange={(e) => setDebitNote(e.target.value)}
            multiline
            minRows={2}
            placeholder="e.g. Admin requested reset of package balances"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDebitTarget(null)} disabled={debitBusy}>Cancel</Button>
          <Button variant="contained" color="error" onClick={submitDebitAll} disabled={debitBusy}>
            {debitBusy ? "Processing..." : "Debit All Balances Now"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
